import { entities } from 'pages/store/schema';
import { getValues, getValue } from 'pages/store/selectors';

import { createSelector } from 'reselect';
import get from 'lodash/get';
import includes from 'lodash/includes';
import concat from 'lodash/concat';
import filter from 'lodash/filter';

import { GatewayType } from './gatewayConfig';

const GatewayFeatures = {
  CLIENT_SIDE_PAYMENTS: 'clientside-payments',
  CLIENT_SIDE_REFUNDS: 'clientside-refunds',
  INTEGRATED_REFUNDS: 'integrated-refunds',
  NEGATIVE_PAYMENTS: 'negative-payments',
  REFUND_BY_REFERENCE: 'refund-by-reference',
  SWIPE: 'swipe',
  SWIPER_PAYMENTS: 'swiper-payments',
  TERMINAL_PAYMENTS: 'terminal-payments',
  TERMINAL_REFUNDS: 'terminal-refunds',
  TERMINAL_VOIDS: 'terminal-voids',
};

export const GatewayParameters = {
  ALLOW_CREDITS: 'allowCredits',
  CHECK_DUPLICATES: 'checkForDuplicateTransactions',
  PARTIAL_APPROVALS: 'partialApprovals',
  SWIPERS_ENABLED: 'swipersEnabled',
  MERCHANT_RECEIPT: 'merchantReceipt',
  SWIPER_TERMINAL_ID: 'swipersTerminalId',
  MAGSTRIPE_FALLBACK: 'magstripeFallback',
};

export const GatewayConfigurationParameters = [
  GatewayParameters.ALLOW_CREDITS,
  GatewayParameters.CHECK_DUPLICATES,
  GatewayParameters.PARTIAL_APPROVALS,
  GatewayParameters.SWIPERS_ENABLED,
  GatewayParameters.MERCHANT_RECEIPT,
  GatewayParameters.SWIPER_TERMINAL_ID,
  GatewayParameters.MAGSTRIPE_FALLBACK,
];

const getFeatures = gateway =>
  concat([], get(gateway, 'capabilities.features.feature', [])).map(feature => feature.id);

const getDisplayName = gateway => get(gateway, 'name', 'Unknown');

// Cayan is a special case because it's not yet supported in the payment service
const hasTerminalSupport = gateway =>
  includes(getFeatures(gateway), GatewayFeatures.TERMINAL_PAYMENTS) ||
  get(gateway, 'gatewayID') === GatewayType.CAYAN;

const iOSGatewayOnly = gateway =>
  includes(getFeatures(gateway), GatewayFeatures.CLIENT_SIDE_PAYMENTS);

const hasSwiperSupportOnly = gateway =>
  includes(getFeatures(gateway), GatewayFeatures.SWIPE) && !hasTerminalSupport(gateway);

const filterCredentials = gatewayParameters =>
  filter(
    gatewayParameters,
    gatewayParameter => !includes(GatewayConfigurationParameters, gatewayParameter.id)
  );

const filterConfiguration = gatewayParameters =>
  filter(gatewayParameters, gatewayParameter =>
    includes(GatewayConfigurationParameters, gatewayParameter.id)
  );

const getParameters = gateway => get(gateway, 'parameters.parameter', []);

export const getGatewayParameters = gatewayId => state =>
  createSelector(getValue(entities.gateway, gatewayId), getParameters)(state);

export const getGatewayCredentialParameters = gatewayId =>
  createSelector(getGatewayParameters(gatewayId), filterCredentials);

export const getGatewayConfigurationParameters = gatewayId =>
  createSelector(getGatewayParameters(gatewayId), filterConfiguration);

export const getAllGatewayCredentialParameters = createSelector(
  getValues(entities.gateway),
  gateways =>
    gateways.map(gateway => ({
      gatewayID: get(gateway, 'gatewayID'),
      parameters: filterCredentials(getParameters(gateway)),
    }))
);

export const getAllGatewayConfigurationParameters = createSelector(
  getValues(entities.gateway),
  gateways =>
    gateways.map(gateway => ({
      gatewayID: get(gateway, 'gatewayID'),
      parameters: filterConfiguration(getParameters(gateway)),
    }))
);

export const getAvailableGatewayTypes = createSelector(getValues(entities.gateway), gateways =>
  gateways.map(gateway => ({
    gatewayID: gateway.gatewayID,
    name: gateway.name,
  }))
);

export const getGatewayDetails = createSelector(getValues(entities.gateway), gateways =>
  gateways.map(gateway => ({
    gatewayID: gateway.gatewayID,
    hasTerminalSupport: hasTerminalSupport(gateway),
    iOSGatewayOnly: iOSGatewayOnly(gateway),
    hasSwiperSupportOnly: hasSwiperSupportOnly(gateway),
    displayName: getDisplayName(gateway),
    features: getFeatures(gateway),
  }))
);
